/**
 * Live keystroke-level character filtering for text inputs, shared by `InputField`/`TextareaField` (see
 * `FormField.tsx`) and any raw `<input>` that can't go through them. This is purely a typing guard (what
 * characters can land in the field); it does not replace the existing submit-time regex validators in
 * `src/features/manage/formRules.ts` and friends, which stay authoritative for "is this value correct".
 */

/** Base character set allowed for each mode, before `extraChars` is added. */
const BASE_CHARS: Record<InputCharMode, string> = {
  text: "A-Za-z\\s'.,\\-",
  number: '0-9',
  alphanumeric: "A-Za-z0-9\\s'.,\\-",
  // A person's or organisation's name ("QA Parent Holdings Ltd 1 & Co", "José O’Brien-Smith (UK)"): any Unicode
  // letter (plus combining marks, so a decomposed accent survives a paste), digits, a plain space and the
  // punctuation names legitimately use. `\s` is deliberately not used, so tabs, newlines and other control
  // characters are still stripped. Needs the `u` flag (see `buildDisallowedPattern`).
  name: "\\p{L}\\p{M}0-9 '’.,\\-&/()",
  // Anything that prints: only control characters (tabs, newlines, bells - \p{Cc}) are stripped. For free-text names
  // that must keep every character the preparer can type (BUG-032: related and controlling party names).
  printable: '',
};

export type InputCharMode = 'text' | 'number' | 'alphanumeric' | 'name' | 'printable';

export interface CharModeOptions {
  /** Restrict typing to this character class. Omit to leave the field unrestricted (e.g. notes, email, password). */
  charMode?: InputCharMode;
  /** Only meaningful for `charMode: 'number'`: allow a single decimal point. */
  allowDecimal?: boolean;
  /** Only meaningful for `charMode: 'number'`: allow a leading minus sign. */
  allowNegative?: boolean;
  /** Only meaningful for `charMode: 'number'`: the most digits allowed before the decimal point (e.g. 15 for DECIMAL(19,4)). */
  maxIntDigits?: number;
  /** Only meaningful for `charMode: 'number'`: the most digits allowed after the decimal point (e.g. 4 for DECIMAL(19,4)). */
  maxDecimals?: number;
  /** Extra literal characters this specific field needs on top of its mode's base set (e.g. `+()` for phone numbers). */
  extraChars?: string;
}

function escapeForCharClass(chars: string): string {
  return chars.replace(/[\\\]^-]/g, '\\$&');
}

/** Builds the "characters NOT allowed" regex for a mode + its options; `null` when nothing should be filtered. */
export function buildDisallowedPattern({
  charMode,
  allowDecimal,
  allowNegative,
  extraChars,
}: CharModeOptions): RegExp | null {
  if (!charMode) return null;
  if (charMode === 'printable') return /\p{Cc}/gu;
  let allowed = BASE_CHARS[charMode];
  if (charMode === 'number') {
    if (allowDecimal) allowed += '.';
    // Escaped so it can never land mid-class next to an extraChars literal and form an accidental range
    // (e.g. unescaped ".-," would be read as the backwards range '.' to ',').
    if (allowNegative) allowed += '\\-';
  }
  if (extraChars) allowed += escapeForCharClass(extraChars);
  // Only `name` uses Unicode property escapes; the other modes keep their original (non-`u`) regex unchanged.
  return new RegExp(`[^${allowed}]`, charMode === 'name' ? 'gu' : 'g');
}

/** Strips every character not permitted by the given mode/options. Empty/undefined values pass through unchanged. */
export function filterByCharMode(value: string, options: CharModeOptions): string {
  const disallowed = buildDisallowedPattern(options);
  if (!disallowed) return value;
  let result = value.replace(disallowed, '');
  // Collapsing a stray sign/decimal point only makes sense for a plain numeric value. A field that also takes
  // `extraChars` (e.g. a phone number allowing "+()- ") is a formatted string, not a number, so every occurrence
  // of its allowed symbols is kept as typed and left to the field's own submit-time pattern to validate.
  if (options.charMode === 'number' && !options.extraChars) {
    if (options.allowNegative) {
      const negative = result.startsWith('-');
      result = (negative ? '-' : '') + result.replace(/-/g, '');
    } else {
      result = result.replace(/-/g, '');
    }
    if (options.allowDecimal) {
      const firstDot = result.indexOf('.');
      if (firstDot !== -1) {
        result = result.slice(0, firstDot + 1) + result.slice(firstDot + 1).replace(/\./g, '');
      }
    }
    const { maxIntDigits, maxDecimals } = options;
    if (maxIntDigits !== undefined || maxDecimals !== undefined) {
      const negative = result.startsWith('-');
      const body = negative ? result.slice(1) : result;
      const dot = body.indexOf('.');
      let integerPart = dot === -1 ? body : body.slice(0, dot);
      let decimalPart = dot === -1 ? null : body.slice(dot + 1);
      if (maxIntDigits !== undefined) integerPart = integerPart.slice(0, maxIntDigits);
      if (decimalPart !== null && maxDecimals !== undefined) decimalPart = decimalPart.slice(0, maxDecimals);
      const keepDot = decimalPart !== null && maxDecimals !== 0;
      result = `${negative ? '-' : ''}${integerPart}${keepDot ? `.${decimalPart}` : ''}`;
    }
  }
  return result;
}
